/**
 * What is required of whom, and what has actually been signed.
 *
 * This panel IS the 21 CFR 11.50(b) manifestation for the electronic display —
 * the manifest page covers the printout, this covers the screen, and the rule
 * asks for both. So every filled row shows the printed name, the date and time,
 * and the MEANING, and no row may omit them.
 *
 * The one thing it must never do is imply cryptography that was not applied.
 * When `cryptographic` is false the summary says so in words, and that line is
 * styled as a fact rather than a warning: an audit-trail signature is a
 * legitimate, Part-11-compliant thing to be, not a degraded one.
 */
import React from 'react'

import { dockedRightPadding } from '../floatingDock'
import type {
  DmsClient, DmsSignatureChallenge, DmsSignatureStatus,
  DmsSignatureVerification,
} from './contracts'
import { InkPad } from './InkPad'
import {
  SIGNATURE_ATTESTATION, SIGNATURE_MEANING_LABELS,
  SIGNATURE_MEANING_STATEMENTS,
} from './contracts'

const MUTED = 'var(--text-muted, #667085)'
const OK = 'var(--success, #1a7f37)'
const WARN = 'var(--warning, #9a6700)'

export interface SignaturePanelProps {
  status: DmsSignatureStatus
  client: DmsClient
  documentId: string
  canSign: boolean
  onChanged?: () => void
}

export function SignaturePanel({
  status, client, documentId, canSign, onChanged,
}: SignaturePanelProps) {
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const [reason, setReason] = React.useState('')
  const [challenge, setChallenge] =
    React.useState<DmsSignatureChallenge | null>(null)
  const [opening, setOpening] = React.useState(false)

  if (!status?.enabled) return null

  const outstanding = status.slots.filter((s) => s.status !== 'signed'
                                                 && s.status !== 'skipped')
  /*
   * MINE, not merely the next one outstanding.
   *
   * A two-party agreement declares both signatures at the same ordinal, so
   * "the first unsigned slot" is whichever sorted first — and the organiser
   * was offered "Sign as artist", read the artist's attestation, typed their
   * password, and only then learned the signature was not theirs to give. A
   * control that refuses AFTER the credential teaches people their password
   * did not work.
   *
   * The server answers `can_sign` per slot — the sequencing floor AND the
   * slot's role, the same two rules signing itself applies — so the button
   * offers the signature this reader can actually give. Absent means an older
   * server that does not report it: fall back to the first outstanding slot,
   * which is exactly what shipped before.
   */
  const knowsCanSign = outstanding.some((s) => s.can_sign !== undefined)
  const next = knowsCanSign
    ? outstanding.find((s) => s.can_sign)
    : outstanding[0]
  /** Outstanding, but none of it is this reader's to sign. */
  const noneMine = knowsCanSign && !next && outstanding.length > 0

  /**
   * Ask what this slot needs, THEN draw the box.
   *
   * The alternative — show a password field always, or never — is wrong in
   * both directions: a `general` document would demand a credential it does
   * not need, and a `part_11` one would offer a Sign button that cannot
   * succeed. It is also where a regime this installation cannot satisfy gets
   * said, while the person can still do something about it.
   */
  async function open() {
    if (!status.request_id) return
    setError('')
    setOpening(true)
    try {
      const got = client.signatureChallenge
        ? await client.signatureChallenge(status.request_id, next?.slot_id)
        // A host that has not implemented it gets the conservative reading:
        // ask for the credential. Being asked for a password you did not need
        // is a smaller failure than signing without one you did.
        : ({ slot_id: next?.slot_id || '', slot_key: '', role: '',
          meaning: next?.meaning || '', profile: '', profile_label: '',
          reauth_mode: 'password', components_required: 2,
          per_signature: true, available: true,
          unavailable_reason: '' } as DmsSignatureChallenge)
      setChallenge(got)
    } catch (err: unknown) {
      setError(detailOf(err) || 'That signature could not be started.')
    } finally {
      setOpening(false)
    }
  }

  async function sign(credential: { password?: string; otp_code?: string;
                                    ink?: string; content_token?: string }) {
    if (!client.sign || !status.request_id) return
    setBusy(true)
    setError('')
    try {
      await client.sign(status.request_id, {
        slot_id: challenge?.slot_id || next?.slot_id,
        reason,
        // The credential itself, never a count. The server verifies it and
        // derives how many identification components were actually proved.
        // `ink` travels the same way and is NOT part of it: a drawn mark is a
        // manifestation, and the server drops one offered for a slot that did
        // not ask so that "which slots carry a mark" stays a property of the
        // document type rather than of whatever the client sent.
        ...credential,
        // WHAT was read, from the challenge that opened this modal. Taken from
        // the challenge rather than recomputed here: the point of the token is
        // that it describes the moment the SIGNER read the step, and a client
        // that refreshed it would be attesting to its own latest fetch.
        content_token: challenge?.content_token || '',
      })
      setReason('')
      setChallenge(null)
      onChanged?.()
    } catch (err: unknown) {
      setError(detailOf(err) || 'That signature could not be applied.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="dms-signatures" style={{ marginTop: 18 }}>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 14, textTransform: 'uppercase',
                     letterSpacing: 0.4 }}>
          Signatures
        </h3>
        <span style={{ fontSize: 13, color: MUTED }}>
          {status.profile_label}
        </span>
      </header>

      <p style={{ fontSize: 13, margin: '6px 0 0',
                  color: status.complete ? OK : WARN }}>
        {status.summary}
      </p>

      <ol style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
        {status.slots.map((slot) => {
          const signed = slot.status === 'signed'
          return (
            <li key={slot.slot_id}
                style={{ padding: '8px 0',
                         borderTop: '1px solid var(--border, #e4e7ec)' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <strong style={{ flex: 1 }}>
                  {SIGNATURE_MEANING_LABELS[slot.meaning] || slot.meaning}
                  {slot.role ? ` · ${slot.role}` : ''}
                </strong>
                <span style={{ fontSize: 13,
                               color: signed ? OK : MUTED }}>
                  {signed ? 'Signed' : 'Awaiting'}
                  {!slot.required && ' (optional)'}
                </span>
              </div>
              {signed ? (
                // 11.50(a): all three, always.
                <div style={{ fontSize: 13, color: MUTED, marginTop: 2 }}>
                  <div>{slot.printed_name}</div>
                  <div>{formatWhen(slot.signed_at)}</div>
                  {slot.reason && <div>Reason: {slot.reason}</div>}
                  {slot.pades_level !== 'none' && (
                    <div>Cryptographic signature ({slot.pades_level})</div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: MUTED, marginTop: 2 }}>
                  {slot.role ? `Awaiting ${slot.role}` : 'Not yet signed'}
                </div>
              )}
            </li>
          )
        })}
      </ol>

      {error && <p style={{ fontSize: 13, color: 'var(--danger, #b42318)' }}>
        {error}
      </p>}

      {canSign && noneMine && (
        // Said plainly rather than by omitting the button: "there is no Sign
        // button" and "this signature is somebody else's" look identical on
        // screen, and only one of them is true.
        <p style={{ marginTop: 8, fontSize: 13, color: MUTED }}>
          {`This document is waiting on ${
            Array.from(new Set(outstanding.map((s) => s.role).filter(Boolean)))
              .join(' and ') || 'another party'}. Nothing here is yours to sign.`}
        </p>
      )}

      {canSign && next && client.sign && (
        // The Assistant's launcher is docked over the bottom-right corner and
        // this panel is the last thing in a scrolling rail, so the Sign button
        // ended up underneath it — unreachable, on the one control the whole
        // screen exists for. `floatingDock` is the convention for that: the
        // launcher CLAIMS the corner and anything running to the right edge
        // reserves what it claimed. Zero when nothing is docked.
        <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap',
                      paddingRight: dockedRightPadding(0) }}>
          <input
            value={reason}
            placeholder="Reason (recorded with your signature)"
            onChange={(e) => setReason(e.target.value)}
            style={{ flex: 1, fontSize: 13 }}
          />
          <button type="button" onClick={open} disabled={busy || opening}>
            {opening ? 'Opening…' : `Sign as ${next.role || next.slot_key}`}
          </button>
        </div>
      )}

      {challenge && (
        <SignModal
          challenge={challenge}
          meaning={challenge.meaning || next?.meaning || ''}
          role={challenge.role || next?.role || ''}
          // Declared by the document TYPE and answered by the server, never
          // guessed here: a pad drawn for a slot that does not want one
          // collects a mark that will be dropped, and a slot that wants one
          // and gets no pad cannot be signed at all.
          wantsInk={Boolean(
            challenge.ink
            ?? status.slots.find((x) => x.slot_id === challenge.slot_id)?.ink
            ?? next?.ink)}
          busy={busy}
          error={error}
          onCancel={() => { setChallenge(null); setError('') }}
          onSign={sign}
        />
      )}

      {status.executed_copy_pending && (
        <PendingExecutedCopy client={client} onRepaired={onChanged} />
      )}

      <EmbeddedSignatures status={status} client={client}
                          documentId={documentId} />

      {client.signatureManifestUrl && status.signed > 0 && (
        <p style={{ marginTop: 8 }}>
          <a href={client.signatureManifestUrl(documentId)}
             target="_blank" rel="noreferrer"
             style={{ fontSize: 13 }}>
            Signature manifest (PDF)
          </a>
        </p>
      )}
    </section>
  )
}

/**
 * The signing modal — 21 CFR 11.100(c)(1) and 11.50(a)(3) in one box.
 *
 * Three things have to be true at the moment somebody signs, and all three are
 * on this screen because a regulation about *informed* signing is not satisfied
 * by a link to a policy document:
 *
 *  * the MEANING is stated as a sentence, not chosen from a list of nouns —
 *    "I performed this step, and the values recorded are what I observed" is
 *    what the person is attesting to, and it is what the record will say;
 *  * the ATTESTATION is shown verbatim: this keystroke is the legal equivalent
 *    of a handwritten signature;
 *  * the CREDENTIAL is collected here, at the moment of signing, and verified
 *    by the server. It is the second identification component, and the client
 *    no longer reports how many it thinks it used.
 *
 * A regime this installation cannot satisfy says so INSTEAD of offering a box:
 * a control that cannot succeed is worse than an absent one, because somebody
 * will keep trying it.
 */
function SignModal({ challenge, meaning, role, wantsInk, busy, error,
                    onCancel, onSign }: {
  challenge: DmsSignatureChallenge
  meaning: string
  role: string
  wantsInk: boolean
  busy: boolean
  error: string
  onCancel: () => void
  onSign: (credential: { password?: string; otp_code?: string;
                         ink?: string }) => void
}) {
  const [secret, setSecret] = React.useState('')
  const [ink, setInk] = React.useState('')
  const mode = challenge.reauth_mode
  const needsSecret = challenge.available
    && mode !== 'none' && challenge.components_required > 1
  const isOtp = mode === 'otp'

  const submit = () => {
    if (!challenge.available) return
    if (needsSecret && !secret) return
    // A slot that ASKED for a mark and got none must not be signed: the filed
    // PDF would print a signature line with nothing on it, which reads exactly
    // like one nobody signed. The server refuses it too — this is the earlier
    // of the two, where the person can still draw one.
    if (wantsInk && !ink) return
    onSign({
      ...(isOtp ? { otp_code: secret } : (needsSecret ? { password: secret }
        : {})),
      ...(wantsInk && ink ? { ink } : {}),
    })
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Apply electronic signature"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel()
        if (e.key === 'Enter' && !busy) submit()
      }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(5,8,13,0.55)',
      }}
    >
      <div style={{
        width: 470, maxWidth: 'calc(100vw - 32px)', borderRadius: 12,
        overflow: 'hidden', background: 'var(--bg, #fff)',
        color: 'var(--text, #111)',
        boxShadow: '0 24px 70px rgba(0,0,0,0.35)',
      }}>
        <div style={{ padding: '16px 20px 12px',
          borderBottom: '1px solid var(--border, #e4e7ec)' }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>
            Apply electronic signature
          </div>
          <div style={{ fontSize: 13, color: MUTED, marginTop: 2 }}>
            {[challenge.profile_label, role].filter(Boolean).join(' · ')}
          </div>
        </div>

        <div style={{ padding: '16px 20px' }}>
          {/* 11.50(a)(3) — the meaning, in words, before anything is typed. */}
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.5,
            textTransform: 'uppercase', color: MUTED }}>
            Meaning of signature
          </div>
          <p style={{ margin: '6px 0 0', fontSize: 15, lineHeight: 1.5 }}>
            {SIGNATURE_MEANING_STATEMENTS[meaning]
              || SIGNATURE_MEANING_LABELS[meaning] || meaning}
          </p>

          {!challenge.available && (
            <p style={{ marginTop: 14, padding: '10px 12px', borderRadius: 8,
              fontSize: 13, lineHeight: 1.55,
              background: 'rgba(180,35,24,0.08)',
              color: 'var(--danger, #b42318)' }}>
              {challenge.unavailable_reason
                || 'This installation cannot satisfy this signing regime.'}
            </p>
          )}

          {wantsInk && (
            <div style={{ marginTop: 16 }}>
              <InkPad onChange={setInk} disabled={busy}
                      label={`Sign as ${role || 'yourself'}`} />
            </div>
          )}

          {needsSecret && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.5,
                textTransform: 'uppercase', color: MUTED, marginTop: 16 }}>
                {isOtp ? 'One-time code' : 'Password'}
              </div>
              <input
                type={isOtp ? 'text' : 'password'}
                value={secret}
                autoFocus
                autoComplete={isOtp ? 'one-time-code' : 'current-password'}
                inputMode={isOtp ? 'numeric' : undefined}
                onChange={(e) => setSecret(e.target.value)}
                style={{
                  width: '100%', marginTop: 7, padding: '9px 12px',
                  borderRadius: 8, fontSize: 15,
                  border: '1px solid var(--border, #d0d5dd)',
                }}
              />
              <p style={{ margin: '6px 0 0', fontSize: 13, color: MUTED }}>
                {challenge.per_signature
                  ? 'Required for each signature you apply.'
                  : 'Required once for this set of signatures.'}
              </p>
            </>
          )}

          {/* 11.100(c)(1), verbatim. Not ours to reword. */}
          <p style={{
            marginTop: 16, padding: '11px 13px', borderRadius: 8,
            fontSize: 13, lineHeight: 1.6, color: MUTED,
            background: 'var(--bg-subtle, #f6f8fa)',
          }}>{SIGNATURE_ATTESTATION}</p>

          {error && (
            <p style={{ marginTop: 10, fontSize: 13,
              color: 'var(--danger, #b42318)' }}>{error}</p>
          )}
        </div>

        <div style={{
          display: 'flex', gap: 10, justifyContent: 'flex-end',
          padding: '12px 20px', borderTop: '1px solid var(--border, #e4e7ec)',
        }}>
          <button type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !challenge.available
              || (needsSecret && !secret) || (wantsInk && !ink)}
          >{busy ? 'Signing…' : 'Sign'}</button>
        </div>
      </div>
    </div>
  )
}

/**
 * What a third party opening the FILE would see.
 *
 * Deliberately separate from the campaign above, because the two answer
 * different questions and a reader needs both. The campaign says who attested
 * to what, and this server recorded it; this says whether the PDF itself
 * carries a signature anybody else can check without asking us.
 *
 * INTEGRITY, TRUST and COVERAGE are rendered as three facts and never folded
 * into one verdict. A self-signed certificate is perfectly intact and entirely
 * untrusted; a signature can be cryptographically perfect and cover only part
 * of the file. One green tick over those would be the most misleading thing
 * this panel could draw.
 *
 * It is checked ON DEMAND, not on every render: verification parses the whole
 * PDF and, for a trusted-chain check, may reach out for revocation
 * information. A reader who wants it asks for it.
 */
function EmbeddedSignatures({ status, client, documentId }: {
  status: DmsSignatureStatus
  client: DmsClient
  documentId: string
}) {
  const [report, setReport] =
    React.useState<DmsSignatureVerification | null>(null)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')

  // Nothing to check until something has been signed, and nothing to offer on
  // a host whose client does not implement it.
  if (!client.verifySignatures || status.signed < 1) return null

  async function check() {
    setBusy(true)
    setError('')
    try {
      setReport(await client.verifySignatures!(documentId))
    } catch (err: unknown) {
      setError(detailOf(err) || 'Those signatures could not be checked.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ marginTop: 12, paddingTop: 10,
                  borderTop: `1px solid var(--border, #e4e7ec)` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.4,
                       textTransform: 'uppercase', color: MUTED, flex: 1 }}>
          In the file itself
        </span>
        <button type="button" onClick={check} disabled={busy}
                style={{ fontSize: 13 }}>
          {busy ? 'Checking…' : report ? 'Check again' : 'Check signatures'}
        </button>
      </div>

      {error && (
        <p style={{ fontSize: 13, marginTop: 6,
                    color: 'var(--danger, #b42318)' }}>{error}</p>
      )}

      {report && !report.available && (
        <p style={{ fontSize: 13, marginTop: 6, color: MUTED }}>
          {report.reason || 'This server cannot check embedded signatures.'}
        </p>
      )}

      {report && report.available && report.count === 0 && (
        <p style={{ fontSize: 13, marginTop: 6, color: MUTED }}>
          {report.readable
            ? 'This file carries no embedded signature. The signatures above '
              + 'are audit-trail records, which is a complete electronic '
              + 'signature — it is simply not one a third party can check on '
              + 'their own.'
            : report.reason || 'This file could not be read.'}
        </p>
      )}

      {report && report.signatures.map((sig) => (
        <div key={sig.field_name}
             style={{ marginTop: 8, fontSize: 13, lineHeight: 1.5 }}>
          <div style={{ fontWeight: 600 }}>{sig.field_name}</div>
          <div style={{ color: MUTED }}>{sig.signer || 'unnamed signer'}</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0 0',
                       display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Fact ok={sig.intact}
                  yes="Unchanged since signing"
                  no="The signed bytes have CHANGED" />
            <Fact ok={sig.trusted}
                  yes="Issuer is trusted here"
                  // Not a failure, and it must not be coloured like one: an
                  // unknown or self-signed issuer is the ordinary case until
                  // trust roots are configured, and the integrity fact beside
                  // it still stands on its own.
                  no="Issuer not recognised here"
                  neutral={!sig.trusted} />
            <Fact ok={sig.covers_whole_document}
                  yes="Covers the whole document"
                  no={sig.coverage
                    ? `Covers ${sig.coverage.toLowerCase().replace(/_/g, ' ')}`
                    : 'Covers part of the document'}
                  // An EARLIER signature covering its own revision rather than
                  // the whole file is correct in a multi-signature document —
                  // later ones are appended after it — so it is stated rather
                  // than flagged.
                  neutral={!sig.covers_whole_document
                           && Boolean(sig.covers_own_revision)} />
          </ul>
          {sig.trust_reason && !sig.trusted && (
            <div style={{ fontSize: 13, color: MUTED, marginTop: 2 }}>
              {sig.trust_reason}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

/**
 * Fully signed, and the copy with the signatures drawn in was never filed.
 *
 * Said plainly and offered as an action, because the alternative is a document
 * that looks finished: the repository holds a version whose signature lines all
 * read "Not yet signed", under a panel saying both parties signed. Both are
 * true about different things, and leaving a reader to work out which one the
 * document is, is the failure this note exists to prevent.
 */
function PendingExecutedCopy({ client, onRepaired }: {
  client: DmsClient
  onRepaired?: () => void
}) {
  const [busy, setBusy] = React.useState(false)
  const [note, setNote] = React.useState('')

  if (!client.repairExecutedCopies) {
    return (
      <p style={{ fontSize: 13, marginTop: 10, color: WARN }}>
        Everybody has signed, but the copy with the signatures drawn into it
        was not filed. The version above is still the one that was signed.
      </p>
    )
  }

  async function repair() {
    setBusy(true)
    setNote('')
    try {
      const result = await client.repairExecutedCopies!()
      if (result.failed?.length) {
        // NAMED, never counted. A repair reporting "1 filed" while silently
        // skipping another is how a missing copy stays missing.
        setNote(result.failed.map((f) => f.error).join('\n'))
        return
      }
      onRepaired?.()
    } catch (err: unknown) {
      setNote(detailOf(err) || 'That copy could not be filed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.5,
                  color: WARN }}>
      <div>
        Everybody has signed, but the copy with the signatures drawn into it
        was not filed — so the version in the repository still shows empty
        signature lines.
      </div>
      <button type="button" onClick={repair} disabled={busy}
              style={{ marginTop: 6, fontSize: 13 }}>
        {busy ? 'Filing…' : 'File the signed copy'}
      </button>
      {note && <div style={{ marginTop: 4,
                             color: 'var(--danger, #b42318)' }}>{note}</div>}
    </div>
  )
}

function Fact({ ok, yes, no, neutral = false }: {
  ok: boolean; yes: string; no: string; neutral?: boolean
}) {
  const colour = ok ? OK : (neutral ? MUTED : 'var(--danger, #b42318)')
  return (
    <li style={{ fontSize: 13, color: colour }}>
      {ok ? '✓' : (neutral ? '·' : '✕')} {ok ? yes : no}
    </li>
  )
}


function detailOf(err: unknown): string {
  // A host client may wrap the transport error (the Run view's `dmsError`
  // does): the readable sentence on `.message`, the original on `.cause`.
  // Read through the wrapper, or every refusal — "that credential was not
  // accepted" included — collapses into the generic line.
  const wrapped = err as { response?: unknown; cause?: unknown;
                           message?: unknown }
  if (!wrapped?.response && wrapped?.cause
      && (wrapped.cause as { response?: unknown }).response) {
    const inner = detailOf(wrapped.cause)
    if (inner) return inner
  }
  if (!wrapped?.response && typeof wrapped?.message === 'string'
      && wrapped.message.trim()) {
    return wrapped.message
  }
  const data = (err as { response?: { data?: unknown } })?.response?.data
  const detail = (data as { detail?: unknown })?.detail
  if (typeof detail === 'string') return detail
  // A 428 carries a structured body — the prompt sentence is inside it.
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const message = String((detail as { message?: string }).message || '')
    if (message) return message
  }
  // No sentence to show — a proxy's HTML error page, a bare 500, or a request
  // the server could not parse (FastAPI's 422 `detail` is a LIST). Say which,
  // so "could not be applied" is never the whole story.
  const status = (err as { response?: { status?: number } })?.response?.status
  if (status) {
    return `The server answered ${status}${
      status === 504 || status === 502
        ? ' — it did not respond in time.'
        : status === 413 ? ' — the drawn signature was too large to send.'
          : '. Nothing was signed.'}`
  }
  return ''
}

function formatWhen(value?: string): string {
  if (!value) return ''
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    // The reader's own timezone, with the absolute value on hover — an
    // approval time that reads differently to two people is a support call.
    return d.toLocaleString()
  } catch {
    return value
  }
}

export default SignaturePanel
